/**
 * Ekip › Organizasyon state (v0.1.150): one GET /team/organization, the company in departments; read again on
 * Tekrar dene. Read only.
 */

import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import type { OrganizationView } from '../api/organization.js';
import { useStore } from './context.js';

export interface OrganizationState {
  view: OrganizationView | null;
  error: string | null;
  loading: boolean;
}

export class OrganizationStore {
  private state: OrganizationState = { view: null, error: null, loading: false };
  private inFlight: Promise<void> | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): OrganizationState => this.state;

  private set(patch: Partial<OrganizationState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  /** Loads once; the screen calls it on mount. */
  ensureLoaded(): Promise<void> {
    if (this.state.view !== null) return Promise.resolve();
    return this.inFlight ?? this.reload();
  }

  reload(): Promise<void> {
    this.set({ loading: true });
    const run = (async () => {
      try {
        this.set({ view: await this.api.getOrganization(), error: null, loading: false });
      } catch (error) {
        this.set({ error: error instanceof Error ? error.message : String(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }
}

export function useOrganization(): { store: OrganizationStore; state: OrganizationState } {
  const store = useStore().organization;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
